import type { ReactElement } from "react";
import { Link } from "react-router-dom";
import { LumiLogo } from "../../shared/components/branding/LumiLogo";

function LegalHeader(): ReactElement {
  return (
    <header className="border-b border-neutral-800">
      <div className="container mx-auto px-8 py-6">
        <Link to="/" className="inline-block">
          <LumiLogo className="h-6" ariaLabel="Lumi - Home" />
        </Link>
      </div>
    </header>
  );
}

function LegalFooter(): ReactElement {
  return (
    <div className="mt-12 pt-8 border-t border-neutral-800">
      <Link to="/" className="text-neutral-400 hover:text-white transition-colors">
        ← Back to Home
      </Link>
    </div>
  );
}

function TOSContent(): ReactElement {
  return (
    <div className="prose prose-invert prose-neutral max-w-none">
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">1. Acceptance of Terms</h2>
        <p className="text-neutral-300 leading-relaxed">
          By accessing and using Lumi ("the Service"), you accept and agree to be bound by the terms and provision of
          this agreement. If you do not agree to these Terms of Service, please do not use the Service.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">2. Use of Service</h2>
        <p className="text-neutral-300 leading-relaxed mb-4">
          Lumi provides streaming video content for personal, non-commercial use. You agree to:
        </p>
        <ul className="list-disc list-inside text-neutral-300 space-y-2 ml-4">
          <li>Use the Service only for lawful purposes</li>
          <li>Not share your account credentials with others</li>
          <li>Not attempt to circumvent any content protection measures</li>
          <li>Not use automated systems to access the Service</li>
          <li>Comply with all applicable laws and regulations</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">3. Account Registration</h2>
        <p className="text-neutral-300 leading-relaxed">
          You must create an account to access certain features. You are responsible for maintaining the confidentiality
          of your account credentials and for all activities that occur under your account. You agree to notify us
          immediately of any unauthorized access to your account.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">4. Content and Intellectual Property</h2>
        <p className="text-neutral-300 leading-relaxed">
          All content provided through the Service, including videos, images, text, and software, is owned by Lumi or
          its content suppliers and is protected by copyright and other intellectual property laws. You may not copy,
          distribute, modify, or create derivative works from any content without express permission.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">5. Trial Period and Subscriptions</h2>
        <p className="text-neutral-300 leading-relaxed">
          New users may be eligible for a free trial period. After the trial ends, continued access requires a paid
          subscription. Subscription terms, pricing, and cancellation policies will be provided at the time of purchase.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">6. Privacy</h2>
        <p className="text-neutral-300 leading-relaxed">
          Your privacy is important to us. Please review our{" "}
          <Link to="/legal/privacy" className="text-lumi-accent hover:underline">
            Privacy Policy
          </Link>{" "}
          to understand how we collect, use, and protect your information.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">7. Termination</h2>
        <p className="text-neutral-300 leading-relaxed">
          We reserve the right to suspend or terminate your account at any time for violations of these Terms of Service
          or for any other reason. You may cancel your account at any time through your account settings.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">8. Disclaimer of Warranties</h2>
        <p className="text-neutral-300 leading-relaxed">
          The Service is provided "as is" without warranties of any kind, either express or implied. We do not warrant
          that the Service will be uninterrupted, secure, or error-free.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">9. Limitation of Liability</h2>
        <p className="text-neutral-300 leading-relaxed">
          Lumi shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting
          from your use or inability to use the Service.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">10. Changes to Terms</h2>
        <p className="text-neutral-300 leading-relaxed">
          We reserve the right to modify these Terms of Service at any time. We will notify users of significant changes
          via email or through the Service. Continued use after changes constitutes acceptance of the new terms.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">11. Contact Information</h2>
        <p className="text-neutral-300 leading-relaxed">
          For questions about these Terms of Service, please contact us at:
          <br />
          <a href="mailto:legal@lumi.theforge.space" className="text-lumi-accent hover:underline">
            legal@lumi.theforge.space
          </a>
        </p>
      </section>
    </div>
  );
}

export function TermsOfService(): ReactElement {
  return (
    <div className="min-h-screen bg-black text-white">
      <LegalHeader />

      <main className="container mx-auto px-8 py-12 max-w-4xl">
        <h1 className="text-4xl font-bold mb-2">Terms of Service</h1>
        <p className="text-neutral-400 mb-8">Effective Date: November 12, 2025</p>

        <TOSContent />
        <LegalFooter />
      </main>
    </div>
  );
}
